import OpenAI from 'openai';
import { collection, query, where, orderBy, getDocs, limit } from 'firebase/firestore';

class OpenAIService {
  constructor() {
    // Note: API key will be handled server-side for security
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Generate executive summary using GPT-3.5-turbo with COMPREHENSIVE crisis data integration
   */
  async generateExecutiveSummary(incidentData, realTimeAnalysis, db = null, currentUser = null) {
    try {
      // CLEAR CACHE TO FORCE FRESH CALLS
      this.clearCache();
      
      // Create cache key based on ALL comprehensive data
      const liveMessages = await this.fetchLiveMessages(incidentData?.incident?.id, db, currentUser);
      const cacheKey = this.createCacheKey(incidentData, realTimeAnalysis, liveMessages);
      
      // Check cache first
      const cached = this.getCachedSummary(cacheKey);
      if (cached) {
        console.log('Using cached executive summary');
        return cached;
      }

      console.log(`🤖 Generating AI summary with COMPREHENSIVE crisis data:`, {
        liveMessages: liveMessages?.length || 0,
        communications: incidentData?.communications?.length || 0,
        documentActivities: incidentData?.documentActivities?.length || 0,
        mediaIntelligence: incidentData?.mediaIntelligence?.length || 0,
        automationEvents: incidentData?.automationEvents?.length || 0,
        siteStatusUpdates: incidentData?.siteStatusUpdates?.length || 0,
        regulatoryActivities: incidentData?.regulatoryActivities?.length || 0,
        timelineEvents: incidentData?.timelineEvents?.length || 0,
        systemEvents: incidentData?.systemEvents?.length || 0,
        digitalProducts: incidentData?.digitalProducts?.length || 0,
        organizationProducts: incidentData?.organizationProducts?.length || 0,
        crisisTemplates: Object.keys(incidentData?.availableCrisisTemplates || {}).length
      });

      // Make API call to server endpoint
      try {
        // Use Firebase Cloud Function instead of local server
        const cloudFunctionUrl = process.env.REACT_APP_FIREBASE_REGION 
          ? `https://${process.env.REACT_APP_FIREBASE_REGION}-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/generateExecutiveSummary`
          : 'https://australia-southeast2-crisiscompass-prod.cloudfunctions.net/generateExecutiveSummary';
        
        console.log('🤖 Calling Cloud Function with comprehensive data + Digital Products:', cloudFunctionUrl);
        
        const response = await fetch(cloudFunctionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            // Core incident data
            incidentData,
            realTimeAnalysis,
            liveMessages: liveMessages || [],
            
            // COMPREHENSIVE CRISIS DATA PACKAGE FOR AI ANALYSIS
            comprehensiveData: {
              // Communication and collaboration intelligence
              communications: incidentData?.communications || [],
              liveChatMessages: incidentData?.liveChatMessages || [],
              documentActivities: incidentData?.documentActivities || [],
              
              // Monitoring and intelligence data
              mediaIntelligence: incidentData?.mediaIntelligence || [],
              automationEvents: incidentData?.automationEvents || [],
              siteStatusUpdates: incidentData?.siteStatusUpdates || [],
              
              // Compliance and governance data
              regulatoryActivities: incidentData?.regulatoryActivities || [],
              contactDirectoryUpdates: incidentData?.contactDirectoryUpdates || [],
              timelineEvents: incidentData?.timelineEvents || [],
              systemEvents: incidentData?.systemEvents || [],
              
              // Enhanced metrics from comprehensive analysis
              communicationMetrics: realTimeAnalysis?.communicationMetrics || {},
              chatMetrics: realTimeAnalysis?.chatMetrics || {},
              documentMetrics: realTimeAnalysis?.documentMetrics || {},
              mediaMetrics: realTimeAnalysis?.mediaMetrics || {},
              automationMetrics: realTimeAnalysis?.automationMetrics || {},
              siteMetrics: realTimeAnalysis?.siteMetrics || {},
              regulatoryMetrics: realTimeAnalysis?.regulatoryMetrics || {},
              timelineMetrics: realTimeAnalysis?.timelineMetrics || {},
              systemMetrics: realTimeAnalysis?.systemMetrics || {},
              
              // CRISIS COMPASS DIGITAL PRODUCTS & RESOURCES FOR AI RECOMMENDATIONS
              digitalProducts: incidentData?.digitalProducts || [],
              organizationProducts: incidentData?.organizationProducts || [],
              availableCrisisTemplates: incidentData?.availableCrisisTemplates || {},
              allDigitalProducts: incidentData?.allDigitalProducts || {},
              digitalProductMetrics: realTimeAnalysis?.digitalProductMetrics || {}
            }
          })
        });

        if (!response.ok) {
          throw new Error(`Server response: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        
        // Cache the result
        this.setCachedSummary(cacheKey, result);
        
        console.log(`✅ AI summary generated successfully with comprehensive data (source: ${result.source})`);
        return result;

      } catch (serverError) {
        console.warn('⚠️ Server/OpenAI unavailable, using enhanced rule-based fallback:', serverError.message);
        
        // Fallback to enhanced rule-based analysis (includes ALL data sources)
        const result = this.generateFallbackSummary(incidentData, realTimeAnalysis);
        
        // Cache the fallback result
        this.setCachedSummary(cacheKey, result);
        
        console.log(`✅ Enhanced rule-based fallback summary generated with comprehensive data`);
        return result;
      }

    } catch (error) {
      console.error('Error generating executive summary:', error);
      
      // Send notification to super admin
      this.notifySuperAdmin(error, incidentData);
      
      // Return enhanced fallback summary
      return this.generateFallbackSummary(incidentData, realTimeAnalysis);
    }
  }

  /**
   * Fetch live chat messages for AI analysis
   */
  async fetchLiveMessages(incidentId, db, currentUser) {
    if (!incidentId || !db || !currentUser?.orgId) {
      console.log('Cannot fetch live messages - missing required data');
      return [];
    }

    try {
      console.log('📨 Fetching live chat messages for AI analysis...');

      // Query last 24 hours of messages for this incident
      const messagesRef = collection(db, 'c3_incidentChats');
      const last24Hours = new Date(Date.now() - 24 * 60 * 60 * 1000);
      
      const q = query(
        messagesRef,
        where('incidentId', '==', incidentId),
        where('orgId', '==', currentUser.orgId),
        orderBy('timestamp', 'desc'),
        limit(50) // Limit to avoid overwhelming the AI
      );

      const snapshot = await getDocs(q);
      
      const messages = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          message: data.message,
          timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : new Date(data.timestamp),
          authorId: data.authorId,
          authorName: data.authorName,
          firstName: data.firstName,
          lastName: data.lastName,
          role: data.role,
          isSystemMessage: data.isSystemMessage || false,
          mediaArticleId: data.mediaArticleId,
          articleRelevanceScore: data.articleRelevanceScore
        };
      });

      // Filter to only messages from last 24 hours and sort chronologically
      const recentMessages = messages
        .filter(msg => msg.timestamp >= last24Hours)
        .sort((a, b) => a.timestamp - b.timestamp);

      console.log(`📊 Found ${recentMessages.length} recent messages for AI analysis`);
      
      // Log message types for debugging
      const systemMessages = recentMessages.filter(m => m.isSystemMessage).length;
      const userMessages = recentMessages.length - systemMessages;
      console.log(`📈 Message breakdown: ${userMessages} user messages, ${systemMessages} system alerts`);

      return recentMessages;

    } catch (error) {
      console.error('Error fetching live messages:', error);
      return []; // Return empty array if fetch fails
    }
  }

  /**
   * Create cache key from incident data (exposed for external cache checking)
   */
  createCacheKey(incidentData, realTimeAnalysis, liveMessages = []) {
    // Include message count and most recent message timestamp in cache key
    const latestMessageTime = liveMessages.length > 0 ? 
      Math.max(...liveMessages.map(m => new Date(m.timestamp).getTime())) : 0;
    
    const keyData = {
      incidentId: incidentData.incident?.id,
      riskScore: realTimeAnalysis.riskScore,
      taskCompletion: realTimeAnalysis.taskCompletionRate,
      staffSafety: realTimeAnalysis.staffSafetyRate,
      incidentAge: realTimeAnalysis.incidentAge,
      messageCount: liveMessages.length,
      latestMessageTime: latestMessageTime,
      timestamp: Math.floor(Date.now() / (5 * 60 * 1000)) // 5-minute buckets
    };
    
    return JSON.stringify(keyData);
  }

  /**
   * Get cached summary if still valid (exposed for external cache checking)
   */
  getCachedSummary(cacheKey) {
    const cached = this.cache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp) < this.cacheTimeout) {
      return cached.data;
    }
    
    // Remove expired cache
    if (cached) {
      this.cache.delete(cacheKey);
    }
    
    return null;
  }

  /**
   * Get cache timeout in milliseconds (for external reference)
   */
  getCacheTimeout() {
    return this.cacheTimeout;
  }

  /**
   * Cache summary with timestamp
   */
  setCachedSummary(cacheKey, data) {
    this.cache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
    
    // Clean up old cache entries (keep last 10)
    if (this.cache.size > 10) {
      const oldestKey = this.cache.keys().next().value;
      this.cache.delete(oldestKey);
    }
  }

  /**
   * Generate enhanced rule-based fallback summary (STRUCTURED FORMAT per briefing specification)
   */
  generateFallbackSummary(incidentData, realTimeAnalysis) {
    const incident = incidentData.incident;
    const analysis = realTimeAnalysis;
    const now = new Date();

    // Generate structured HTML summary per briefing specification
    let summaryHtml = '';

    // Italicized timestamp header
    summaryHtml += `<p style="font-style: italic; color: #000; margin-bottom: 16px;">This is an AI-generated crisis summary, drawing on all available crisis information, live chat inputs from the CMT and news/media intelligence. Generated ${now.toLocaleString()}</p>`;

    // Extract live chat context for WHAT section
    const liveMessages = incidentData?.liveChatMessages || [];
    const recentMessages = liveMessages.slice(-5); // Get last 5 messages for context
    let liveContext = '';
    if (recentMessages.length > 0) {
      const contextSnippets = recentMessages.map(msg => msg.message).join('. ');
      liveContext = ` Live updates include: ${contextSnippets}.`;
    }

    // WHAT section with live context
    summaryHtml += `<p><strong>WHAT:</strong> ${incident?.crisisType || 'Crisis'} incident affecting ${incident?.title || 'the organization'}. Current status: ${incident?.status?.toUpperCase() || 'ACTIVE'}. Severity level: ${incident?.severity?.toUpperCase() || 'STANDARD'}.${liveContext} This incident has been active for ${analysis.incidentAge || 0} hours with ${analysis.totalTasks || 0} response tasks assigned (${(analysis.taskCompletionRate || 0).toFixed(0)}% complete).</p>`;

    summaryHtml += `<br>`;

    // WHERE section  
    summaryHtml += `<p><strong>WHERE:</strong> ${incident?.location || 'Multiple locations'}. ${analysis.siteMetrics?.total || 0} sites are being monitored with ${analysis.siteMetrics?.recent || 0} recent status updates. Infrastructure impact across operational areas with varying degrees of disruption.</p>`;

    summaryHtml += `<br>`;

    // WHEN section
    const activationTime = incident?.occurredAt?.toDate ? incident.occurredAt.toDate() : 
                          incident?.createdAt?.toDate ? incident.createdAt.toDate() : now;
    summaryHtml += `<p><strong>WHEN:</strong> Incident activated at ${activationTime.toLocaleString()}. Duration: ${analysis.incidentAge || 0} hours. Last updated by ${incident?.lastUpdatedBy || 'System'}. Recent activity: ${analysis.recentActivity || 0} updates in the past 6 hours across all monitoring systems.</p>`;

    summaryHtml += `<br>`;

    // CAUSE section
    summaryHtml += `<p><strong>CAUSE:</strong> ${incident?.description || 'Incident cause under investigation'}. Root cause analysis ongoing. Contributing factors being assessed through comprehensive data collection across ${[
      incidentData?.communications?.length || 0,
      incidentData?.liveChatMessages?.length || 0,
      incidentData?.documentActivities?.length || 0,
      incidentData?.mediaIntelligence?.length || 0,
      incidentData?.automationEvents?.length || 0,
      incidentData?.siteStatusUpdates?.length || 0,
      incidentData?.regulatoryActivities?.length || 0,
      incidentData?.timelineEvents?.length || 0,
      incidentData?.systemEvents?.length || 0
    ].reduce((a, b) => a + b, 0)} data sources.</p>`;

    summaryHtml += `<br>`;

    // IMPACT section
    summaryHtml += `<p><strong>IMPACT:</strong> ${analysis.totalStaff || 0} staff members tracked with ${(analysis.staffSafetyRate || 0).toFixed(0)}% confirmed safe. ${analysis.overdueTasks || 0} critical tasks overdue. ${analysis.communicationMetrics?.recent || 0} emergency communications sent. ${analysis.mediaMetrics?.recent || 0} media mentions in past 6 hours. ${analysis.regulatoryMetrics?.recent || 0} regulatory compliance activities triggered.</p>`;

    summaryHtml += `<br>`;

    // RESPONSE section
    summaryHtml += `<p><strong>RESPONSE:</strong> Crisis management team activated with ${analysis.totalMilestones || 0} milestones established (${(analysis.milestoneCompletionRate || 0).toFixed(0)}% achieved). ${analysis.documentMetrics?.recent || 0} critical documents processed. ${analysis.automationMetrics?.recent || 0} automated response protocols triggered. ${analysis.chatMetrics?.participants?.length || 0} team members actively coordinating via live chat.</p>`;

    summaryHtml += `<br>`;

    // RECOMMENDATION/S section - SHORT AND SHARP
    let recommendations = [];
    if (analysis.staffSafetyRate < 95) {
      recommendations.push('Verify staff safety immediately');
    }
    if (analysis.taskCompletionRate < 70) {
      recommendations.push('Escalate task completion');
    }
    if (analysis.overdueTasks > 0) {
      recommendations.push(`Complete ${analysis.overdueTasks} overdue tasks`);
    }
    if (recommendations.length === 0) {
      recommendations.push('Maintain current protocols');
    }
    summaryHtml += `<p><strong>RECOMMENDATION/S:</strong> ${recommendations.join('. ')}.</p>`;

    summaryHtml += `<br>`;

    // IMMEDIATE ACTION/S REQUIRED section
    let immediateActions = [];
    if (analysis.unaccountedStaff > 0) {
      immediateActions.push(`Locate ${analysis.unaccountedStaff} unaccounted staff`);
    }
    if (analysis.needHelpStaff > 0) {
      immediateActions.push(`Assist ${analysis.needHelpStaff} staff requiring help`);
    }
    if (analysis.overdueTasks > 0) {
      immediateActions.push(`Complete ${analysis.overdueTasks} overdue tasks`);
    }
    
    // Add crisis template immediate actions if available
    if (incidentData?.availableCrisisTemplates?.roles?.cmt_leader?.phases?.immediate) {
      const cmtTasks = incidentData.availableCrisisTemplates.roles.cmt_leader.phases.immediate.slice(0, 3);
      cmtTasks.forEach(task => {
        immediateActions.push(task.name);
      });
    }
    
    if (immediateActions.length === 0) {
      immediateActions.push('Monitor situation and maintain operational readiness');
    }
    summaryHtml += `<p><strong>IMMEDIATE ACTION/S REQUIRED:</strong> ${immediateActions.slice(0, 5).join('. ')}.</p>`;

    summaryHtml += `<br>`;

    // Staff impact summary section
    summaryHtml += `<p><strong>STAFF IMPACT SUMMARY:</strong> Total staff tracked: ${analysis.totalStaff || 0}. Safe: ${analysis.safeStaff || 0} (${(analysis.staffSafetyRate || 0).toFixed(0)}%)`;
    if (analysis.needHelpStaff > 0) summaryHtml += `, Need assistance: ${analysis.needHelpStaff}`;
    if (analysis.unaccountedStaff > 0) summaryHtml += `, Unaccounted: ${analysis.unaccountedStaff}`;
    if (analysis.hospitalisedStaff > 0) summaryHtml += `, Hospitalized: ${analysis.hospitalisedStaff}`;
    if (analysis.deceasedStaff > 0) summaryHtml += `, Deceased: ${analysis.deceasedStaff}`;
    summaryHtml += `. Staff safety status: ${analysis.staffSafetyRate >= 95 ? 'EXCELLENT' : analysis.staffSafetyRate >= 80 ? 'GOOD' : 'REQUIRES ATTENTION'}.</p>`;

    summaryHtml += `<br>`;

    // Site status summary section
    summaryHtml += `<p><strong>SITE STATUS SUMMARY:</strong> ${analysis.siteMetrics?.total || 0} sites monitored with ${analysis.siteMetrics?.recent || 0} recent updates. Site statuses: ${analysis.siteMetrics?.statuses?.join(', ') || 'Status pending'}. Infrastructure impact varies by location with ongoing assessment of operational capacity and recovery requirements.</p>`;

    return {
      summary: summaryHtml,
      source: 'structured_fallback',
      timestamp: new Date().toISOString(),
      confidence: 0.85,
      liveMessagesIncluded: recentMessages.length,
      comprehensiveDataIncluded: true,
      structuredFormat: true
    };
  }



  /**
   * Notify super admin of OpenAI failures
   */
  async notifySuperAdmin(error, incidentData) {
    try {
      // TEMPORARY: API server not available, log locally instead
      console.warn('⚠️ Admin notification (API server not available):', {
        type: 'openai_failure',
        error: error.message,
        incidentId: incidentData.incident?.id,
        timestamp: new Date().toISOString()
      });
    } catch (notificationError) {
      console.error('Failed to notify super admin:', notificationError);
    }
  }

  /**
   * Clear cache (useful for testing)
   */
  clearCache() {
    this.cache.clear();
    console.log('🗑️ OpenAI service cache cleared');
  }
}

// Export singleton instance
export default new OpenAIService(); 